<mat-expansion-panel [expanded]="initialExpanded" (opened)="toggleExpanded()" (closed)="toggleExpanded()">
    <mat-expansion-panel-header>
        <mat-panel-title>{{heading}}
            <ng-container *ngFor="let action of actions">
                <mat-icon class="m-l-10 m-b-5" (click)="handleActionClick($event,action)" [matTooltip]="action.tooltip"
                    color="primary">{{action.icon}}</mat-icon>
            </ng-container>
        </mat-panel-title>
        <!-- Filter trigger in the HEADER, following the clinical timeline
             (`patient-clinical-timeline.component.html:8-13`). `$event.stopPropagation()` on the
             BUTTON is not optional: without it the click reaches the expansion-panel header and
             collapses the section the moment the menu opens. -->
        <button mat-icon-button [matMenuTriggerFor]="riskFiltersMenu"
            (click)="$event.stopPropagation()" *ngIf="hasAnyItems"
            [matTooltip]="isFiltered ? 'Filters (' + activeFilterCount + ' active)' : 'Filter risk items'">
            <mat-icon [matBadge]="activeFilterCount" [matBadgeHidden]="!isFiltered" matBadgeSize="small"
                matBadgeColor="warn">filter_list</mat-icon>
        </button>

        <mat-menu #riskFiltersMenu="matMenu" class="filter-menu">
            <div mat-menu-content class="filter-menu-content" (click)="$event.stopPropagation()">
                <div class="filter-options">
                    <h4>Filter issues</h4>

                    <!-- L2 — the D7 taxonomy and the severity vocabulary both arrive BY IMPORT.
                         Neither list is restated here; a second copy is a list that can be
                         relaxed in one place and not the other. -->
                    <mat-form-field appearance="outline" class="risk-filter-field"
                        (click)="$event.stopPropagation()" (keydown)="handleMenuKeydown($event)">
                        <mat-label>Category</mat-label>
                        <mat-select [(ngModel)]="categoryFilter" (selectionChange)="applyFilter()">
                            <mat-option value="">All categories</mat-option>
                            <mat-option *ngFor="let category of categoryOptions" [value]="category">
                                {{ categoryLabel(category) }}
                            </mat-option>
                        </mat-select>
                    </mat-form-field>

                    <mat-form-field appearance="outline" class="risk-filter-field"
                        (click)="$event.stopPropagation()" (keydown)="handleMenuKeydown($event)">
                        <mat-label>Severity</mat-label>
                        <mat-select [(ngModel)]="severityFilter" (selectionChange)="applyFilter()">
                            <mat-option value="">All severities</mat-option>
                            <mat-option *ngFor="let severity of severityOptions" [value]="severity">
                                {{ severityLabel(severity) }}
                            </mat-option>
                        </mat-select>
                    </mat-form-field>

                    <mat-form-field appearance="outline" class="risk-filter-field"
                        (click)="$event.stopPropagation()" (keydown)="handleMenuKeydown($event)">
                        <mat-label>Status</mat-label>
                        <mat-select [(ngModel)]="statusFilter" (selectionChange)="applyFilter()">
                            <mat-option value="">All statuses</mat-option>
                            <mat-option *ngFor="let status of statusOptions" [value]="status">{{ status }}</mat-option>
                        </mat-select>
                    </mat-form-field>

                    <button mat-stroked-button class="risk-clear-filters" *ngIf="isFiltered"
                        (click)="$event.stopPropagation(); clearFilter()">
                        <mat-icon>filter_alt_off</mat-icon>
                        Clear filters
                    </button>
                </div>
            </div>
        </mat-menu>
    </mat-expansion-panel-header>
    <div class="panel-info">

        <ng-container *ngIf="listData; else listLoading">
            <ng-container *ngIf="listData.length; else noResults">
                <ng-container *ngFor="let group of listData">
                    <!-- The day the issue happened AT THE FACILITY (`dateOfIssue`), not the day it
                         was captured. See the component header. -->
                    <div class="list-header">
                        {{ group.date | date: 'longDate' }}
                    </div>
                    <mat-list class="full-width">
                        <mat-list-item *ngFor="let item of group.items" (click)="loadItem(item)" class="auto-height">
                            <div class="list-item">
                                <div class="list-item-details">
                                    <div matLine class="list-item-title">
                                        <div class="full-height">
                                            <div class="risk-severity-indicator"
                                                [ngClass]="(item.severity || 'unrated') | lowercase"
                                                [matTooltip]="'Severity is ' + severityLabel(item.severity)">
                                            </div>
                                        </div>
                                        <span>{{ item.issue }}</span>
                                    </div>

                                    <div matLine class="list-item-meta">
                                        <span class="risk-classification">{{ riskTitle(item) }}</span>
                                        <span class="risk-facility">{{ riskFacility(item) }}</span>
                                    </div>

                                    <!-- The attachments, mirroring how the timeline renders a
                                         note's media (`patient-clinical-timeline.component.html:147`).

                                         `app-file-gallery` takes RAW server rows: its `ngOnChanges`
                                         spots a `mediaUrl` on the first element and runs
                                         `mapMediaToFileObject` itself — which is also what hides the
                                         PDF row and shows its rasterised pages instead (§29).

                                         `$event.stopPropagation()` because the row itself navigates
                                         to the item: without it, opening a photograph would also
                                         leave the page it was opened from.

                                         `deleteEnabled` false — evidence must not be removable by a
                                         stray tap in a list. Deletion lives on the detail page. -->
                                    <div class="risk-item-media" *ngIf="item.media?.length"
                                        (click)="$event.stopPropagation()">
                                        <app-file-gallery [fileObjects]="item.media" [deleteEnabled]="false">
                                        </app-file-gallery>
                                    </div>

                                    <div matLine class="list-item-footer">
                                        <!-- D9 — a Dismissed item and an Open one must never look
                                             identical on a clinical record. -->
                                        <div *ngIf="item.status">Status: {{ item.status }}</div>
                                        <div *ngIf="item.updatedAt > item.createdAt">
                                            Edited {{ item.updatedAt | dateDiff }} ago
                                        </div>
                                    </div>
                                </div>
                                <div class="view-icon">
                                    <mat-icon>open_in_new</mat-icon>
                                </div>
                            </div>
                        </mat-list-item>
                    </mat-list>
                </ng-container>
            </ng-container>
        </ng-container>
    </div>
</mat-expansion-panel>

<ng-template #listLoading>
    <div class="list-loading">
        <mat-spinner color="primary" mode="indeterminate" diameter="20" style="float:right;"></mat-spinner>
    </div>
</ng-template>

<ng-template #noResults>
    <p class="no-records-message" *ngIf="!isFiltered">No results found.</p>
    <p class="no-records-message" *ngIf="isFiltered">No issues in this category.</p>
</ng-template>
